/**
 * 跨项目执行队列总览（ADM-LP-S018）：只读列出全部项目的后台执行批次。
 * 点击行进入所属项目的执行详情，取消 / 重试沿用项目内执行抽屉；不展示预计完成时间。
 */
import { useEffect, useState } from 'react';
import {
  type ProjectOperation,
  type ProjectQueueExecution,
  type ProjectQueueState,
  useLandingProjectExecutionQueue,
} from '~/data/queries';
import { fmtDateTime } from '~/lib/format';
import { landingAiProviderLabel } from '~/lib/landing-ai-provider-label';
import { Drawer } from '~/ui/Dialog';
import { FilterButton } from '~/ui/FilterButton';
import { LangChip } from '~/ui/LangChip';
import { Pagination } from '~/ui/Pagination';
import { StatusChip } from '~/ui/StatusChip';
import { Table } from '~/ui/Table';
import { LandingProjectExecutionDrawer } from './LandingProjectExecutionDrawer';
import { ProjectState, projectOperationLabel, projectPhaseLabel } from './landing-project-display';

const STATES: { value: ProjectQueueState; label: string }[] = [
  { value: 'active', label: '进行中' },
  { value: 'finished', label: '已结束' },
  { value: 'all', label: '全部' },
];
const OPERATIONS: ProjectOperation[] = ['review', 'desensitize', 'generate', 'publish'];

/** 排队信息只陈述查询时刻的事实：服务商与排在前面的数量，不换算成时长或位次承诺。 */
function QueuePosition({ row }: { row: ProjectQueueExecution }) {
  if (row.provider === null || row.queue_ahead === null) return <span className="muted">—</span>;
  return (
    <div className="vstack" style={{ gap: 2 }}>
      <span className="mono">{landingAiProviderLabel(row.provider)}</span>
      <span className="muted">
        {row.queue_ahead > 0
          ? `前面还有 ${row.queue_ahead} 个章节处理`
          : row.status === 'RUNNING'
            ? '正在执行'
            : '前面没有更早的处理'}
      </span>
    </div>
  );
}

export function LandingExecutionQueueDrawer({ onClose }: { onClose: () => void }) {
  const [state, setState] = useState<ProjectQueueState>('active');
  const [operation, setOperation] = useState<ProjectOperation | undefined>();
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<{ projectId: string; executionId: string } | null>(null);
  const query = useLandingProjectExecutionQueue({
    page,
    state,
    operation,
    // 查看某条详情时暂停总览轮询，返回后再恢复。
    enabled: detail === null,
  });
  // 轮询期间批次陆续结束会让总数缩到当前页之前：越界空页既无行也不再轮询，故收回最后一页。
  const lastPage = Math.max(1, Math.ceil((query.data?.total ?? 0) / 20));
  useEffect(() => {
    if (query.data && page > lastPage) setPage(lastPage);
  }, [query.data, page, lastPage]);
  if (detail)
    return (
      <LandingProjectExecutionDrawer
        projectId={detail.projectId}
        executionId={detail.executionId}
        onClose={() => setDetail(null)}
      />
    );
  const rows = query.data?.data ?? [];
  return (
    <Drawer
      title="执行队列"
      subtitle="全部项目的 AI 审核、AI 脱敏、HTML 生成与发布批次。同一模型服务商同时执行的章节处理数按集成中配置的并发数，不保证严格按提交先后执行。"
      width={960}
      onClose={onClose}
      footer={
        <button type="button" className="btn" onClick={onClose}>
          关闭
        </button>
      }
    >
      <div className="vstack" style={{ padding: 18, gap: 12 }}>
        <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
          <div className="seg" role="group" aria-label="执行范围">
            {STATES.map((option) => (
              <button
                key={option.value}
                type="button"
                className={state === option.value ? 'on' : ''}
                aria-pressed={state === option.value}
                onClick={() => {
                  setState(option.value);
                  setPage(1);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
          <FilterButton
            label="操作"
            multi={false}
            options={OPERATIONS.map((value) => ({
              value,
              label: projectOperationLabel[value] ?? value,
            }))}
            value={operation ? [operation] : []}
            onChange={(next) => {
              setOperation(OPERATIONS.find((value) => value === next[0]));
              setPage(1);
            }}
          />
          <span className="muted">
            {`${state === 'active' ? '按提交先后排列' : '按提交时间倒序'} · 共 ${query.data?.total ?? 0} 批`}
          </span>
        </div>
        {query.isPending ? (
          <p>加载执行队列…</p>
        ) : query.isError ? (
          <p role="alert">
            执行队列加载失败：{query.error.message}
            <button type="button" className="btn" onClick={() => void query.refetch()}>
              重新加载
            </button>
          </p>
        ) : rows.length === 0 ? (
          <p className="muted">
            {state === 'active' ? '当前没有排队或执行中的批次。' : '没有符合条件的执行批次。'}
          </p>
        ) : (
          <Table className="landing-content-table" style={{ minWidth: 880, tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 240 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 200 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 200 }} />
            </colgroup>
            <thead>
              <tr>
                <th>项目 / 书籍</th>
                <th>操作</th>
                <th>状态与完成量</th>
                <th>模型排队</th>
                <th>提交人 / 时间</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setDetail({ projectId: row.project.id, executionId: row.id })}
                >
                  <td style={{ overflowWrap: 'anywhere' }}>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      style={{ padding: 0, fontWeight: 500 }}
                      aria-label={`查看 ${row.project.name} 的执行详情`}
                      onClick={(event) => {
                        event.stopPropagation();
                        setDetail({ projectId: row.project.id, executionId: row.id });
                      }}
                    >
                      {row.project.name}
                    </button>
                    <div className="hstack" style={{ gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                      <span className="mono muted">{row.project.code}</span>
                      {row.project.status === 'ARCHIVED' && (
                        <StatusChip status="ARCHIVED" label="已归档" tone="default" />
                      )}
                    </div>
                    <div className="hstack" style={{ gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                      <span className="mono">{row.novel.book_code}</span>
                      <LangChip code={row.novel.locale} />
                    </div>
                    <div className="muted" dir="auto">
                      {row.novel.title}
                    </div>
                  </td>
                  <td>{projectOperationLabel[row.operation] ?? row.operation}</td>
                  <td>
                    <ProjectState status={row.status} />
                    <div>{projectPhaseLabel(row.phase)}</div>
                    <div className="mono muted">
                      完成 {row.completed}/{row.total}
                      {row.reused ? ` · 复用 ${row.reused}` : ''}
                      {row.failed ? ` · 失败 ${row.failed}` : ''}
                    </div>
                  </td>
                  <td>
                    <QueuePosition row={row} />
                  </td>
                  <td>
                    <div>{row.created_by.nickname ?? '账号不存在'}</div>
                    <div className="mono muted">{fmtDateTime(row.created_at)}</div>
                    {row.heartbeat_at && !row.finished_at && (
                      <div className="mono muted">最近心跳 {fmtDateTime(row.heartbeat_at)}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <Pagination total={query.data?.total ?? 0} page={page} pageSize={20} onChange={setPage} />
      </div>
    </Drawer>
  );
}
